import React, { useCallback, useEffect, useRef, useState } from 'react';
import eventBus from '../EventBus';
import { MOBILE_BREAKPOINT } from '../../World/deskSceneConfig';
import { CURSOR_LOADING_CODE_SNIPPET } from './cursorLoadingCodeSnippet';
import { CURSOR_LOADING_FILE_TREE, FileTreeItem } from './cursorLoadingFileTree';
import CodeBlock from './CodeBlock';
import {
    FolderClosed,
    FolderOpen,
    getFileIcon,
} from './CursorLoadingIcons';

type LoadingProps = {};

type LoadingPhase = 'black' | 'tv_flash' | 'cursor_popup' | 'terminal_running' | 'tv_off' | 'done';

const BUILD_DURATION_MS = 4200;
const TV_OFF_MS = 220;

const SIDEBAR_INDENT = 4;
const CHEVRON_STYLE: React.CSSProperties = { width: 12, fontSize: 10, color: 'rgba(255,255,255,0.7)', marginRight: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };

function renderFileTree(items: FileTreeItem[], depth: number = 0): React.ReactNode {
    return items.map((item) => {
        if (item.type === 'folder') {
            const isOpen = item.open === true;
            const Folder = isOpen ? FolderOpen : FolderClosed;
            return (
                <div key={item.name} style={{ marginLeft: depth * SIDEBAR_INDENT }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0', color: '#9cdcfe', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                        <span style={CHEVRON_STYLE}>{isOpen ? '\u25BC' : '\u203A'}</span>
                        <Folder width={14} height={12} />
                        <span style={{ fontSize: 12 }}>{item.name}</span>
                    </div>
                    {item.children && isOpen && (
                        <div style={{ marginLeft: 2 }}>{renderFileTree(item.children, depth + 1)}</div>
                    )}
                </div>
            );
        }
        const FileIconComponent = getFileIcon(item.name);
        return (
            <div key={item.name} style={{ display: 'flex', alignItems: 'center', marginLeft: depth * SIDEBAR_INDENT + 2, padding: '2px 0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                <span style={{ ...CHEVRON_STYLE, opacity: 0 }}>{'\u203A'}</span>
                <FileIconComponent />
                <span style={{ fontSize: 12, color: '#ce9178' }}>{item.name}</span>
            </div>
        );
    });
}

const SendIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
    </svg>
);

const iconSize = 18;
const HeaderIconSidebarLeft = () => (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <path d="M4 4h4v16H4V4zm6 0h10v2H10V4zm0 6h10v2H10v-2zm0 6h10v2H10v-2z" fill="currentColor" />
    </svg>
);
const HeaderIconPanelBottom = () => (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <path d="M4 8h16v2H4V8zm0 6h16v2H4v-2z" fill="currentColor" />
    </svg>
);
const HeaderIconSettings = () => (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor" />
    </svg>
);

/** Single source: same matchMedia as Sizes so loading and 3D app agree on mobile/portrait. */
function getMobileAndPortrait(): { mobile: boolean; portrait: boolean } {
    if (typeof window === 'undefined') return { mobile: false, portrait: false };
    const mobile = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
    const portrait = window.matchMedia('(orientation: portrait)').matches || (mobile && window.innerHeight > window.innerWidth);
    return { mobile, portrait };
}

/** Rotate phone icon: device body + curved arrow outside (top-right) suggesting rotation */
const RotatePhoneIcon = ({ size = 80 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <rect x="6" y="2" width="12" height="20" rx="2" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" fill="none" />
        <circle cx="12" cy="18" r="1" fill="rgba(255,255,255,0.7)" />
        {/* Curved arrow outside top-right: quarter arc from (19,2) to (22,5) then arrowhead */}
        <path
            d="M19 2 A4 4 0 0 1 22 5"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
        />
        <path d="M20.5 4.5 L22 5 L21 6.2" stroke="rgba(255,255,255,0.85)" strokeWidth="1" fill="none" strokeLinejoin="round" />
    </svg>
);

const LoadingScreen: React.FC<LoadingProps> = () => {
    const [progress, setProgress] = useState(0);
    const [toLoad, setToLoad] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [overlayOpacity, setLoadingOverlayOpacity] = useState(1);
    const [webGLError, setWebGLError] = useState(false);
    const [webGLErrorOpacity, setWebGLErrorOpacity] = useState(0);
    const initial = typeof window !== 'undefined' ? getMobileAndPortrait() : { mobile: false, portrait: false };
    const [mobileWarning, setMobileWarning] = useState(initial.mobile);
    const [isPortrait, setIsPortrait] = useState(initial.portrait);
    /** Shown only on mobile, before any loading UI. User taps Continue to proceed. */
    const [mobilePreWarningVisible, setMobilePreWarningVisible] = useState(initial.mobile);
    /** Shown only on desktop, before Cursor loader. User clicks Start to see loader. */
    const [desktopPreWelcomeVisible, setDesktopPreWelcomeVisible] = useState(!initial.mobile);

    const [phase, setPhase] = useState<LoadingPhase>('black');
    const [cursorPopupVisible, setCursorPopupVisible] = useState(false);
    const [buildProgress, setBuildProgress] = useState(0);
    const [terminalLines, setTerminalLines] = useState<string[]>(['$ Loading assets...', '']);
    const [tvOffFlash, setTvOffFlash] = useState(false);
    const [autoCursorVisible, setAutoCursorVisible] = useState(false);
    const startButtonRef = useRef<HTMLButtonElement>(null);
    const buildIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const resourcesRef = useRef<string[]>([]);
    const terminalScrollRef = useRef<HTMLDivElement>(null);
    const hasShownStartingRef = useRef(false);

    const onResize = () => {
        const { mobile, portrait } = getMobileAndPortrait();
        setMobileWarning(mobile);
        setIsPortrait(portrait);
    };

    const onMobilePreWarningContinue = useCallback(() => {
        setMobilePreWarningVisible(false);
        setCursorPopupVisible(true);
        setPhase('cursor_popup');
    }, []);

    const onDesktopPreWelcomeStart = useCallback(() => {
        setDesktopPreWelcomeVisible(false);
        setCursorPopupVisible(true);
        setPhase('cursor_popup');
    }, []);
    useEffect(() => {
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        const vv = (window as Window & { visualViewport?: { addEventListener: (e: string, f: () => void) => void; removeEventListener: (e: string, f: () => void) => void } }).visualViewport;
        vv?.addEventListener?.('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
            vv?.removeEventListener?.('resize', onResize);
        };
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        // Skip loading screen: add ?skipLoading or ?debug to the URL (e.g. https://yoursite.com/?skipLoading)
        if (urlParams.has('debug') || urlParams.has('skipLoading')) {
            start();
            return;
        }
        if (!detectWebGLContext()) {
            setWebGLError(true);
            return;
        }
        // Re-check dimensions after viewport settles (mobile often reports wrong size on first paint)
        const applyDesktopOrMobile = () => {
            const { mobile, portrait } = getMobileAndPortrait();
            setMobileWarning(mobile);
            setIsPortrait(portrait);
            setMobilePreWarningVisible(mobile);
            if (mobile) {
                setCursorPopupVisible(false);
            } else {
                setDesktopPreWelcomeVisible(true);
                setCursorPopupVisible(false);
                setPhase('cursor_popup');
            }
        };
        applyDesktopOrMobile();
        const t1 = setTimeout(applyDesktopOrMobile, 100);
        const t2 = setTimeout(applyDesktopOrMobile, 350);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    useEffect(() => {
        const handler = (data: { progress: number; toLoad: number; loaded: number; sourceName: string }) => {
            setProgress(data.progress);
            setToLoad(data.toLoad);
            setLoaded(data.loaded);
            const arr = resourcesRef.current;
            arr.push(`Loaded ${data.sourceName} ... ${Math.round(data.progress * 100)}%`);
            if (arr.length > 12) arr.shift();
            // Update terminal with loading progress while assets load
            if (data.progress < 1) {
                setTerminalLines(['$ Loading assets...', '', ...arr.slice(-10)]);
            }
        };
        eventBus.on('loadedSource', handler);
        return () => eventBus.remove?.('loadedSource', handler);
    }, []);

    useEffect(() => {
        if (webGLError) {
            const t = setTimeout(() => setWebGLErrorOpacity(1), 500);
            return () => clearTimeout(t);
        }
    }, [webGLError]);

    const start = useCallback(() => {
        if (buildIntervalRef.current) {
            clearInterval(buildIntervalRef.current);
            buildIntervalRef.current = null;
        }
        document.body.classList.add('loaded');
        setLoadingOverlayOpacity(0);
        eventBus.dispatch('loadingScreenDone', {});
        if (typeof window !== 'undefined' && window.self !== window.top) {
            window.parent.postMessage({ type: 'portfolio-loaded' }, '*');
        }
        const ui = document.getElementById('ui');
        if (ui) ui.style.pointerEvents = 'none';
    }, []);

    const onStartClick = useCallback(() => {
        if (phase !== 'cursor_popup') return;
        setPhase('terminal_running');
        setTerminalLines(['$ npm run build', '']);
        const startTime = Date.now();
        const steps: { at: number; line: string }[] = [
            { at: 0.08, line: '> portfolio-website@1.0.0 build' },
            { at: 0.12, line: '> webpack --config bundler/webpack.prod.js' },
            { at: 0.18, line: '' },
            { at: 0.22, line: 'Building modules...' },
            { at: 0.35, line: 'Compiling assets...' },
            { at: 0.50, line: 'Optimizing chunks...' },
            { at: 0.65, line: 'Processing CSS...' },
            { at: 0.78, line: 'Generating output...' },
            { at: 0.92, line: 'Build completed successfully.' },
            { at: 1.0, line: '' },
        ];
        buildIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const p = Math.min(1, elapsed / BUILD_DURATION_MS);
            setBuildProgress(p * 100);
            const lines = ['$ npm run build', '', ...steps.filter((s) => s.at <= p).map((s) => s.line)];
            setTerminalLines(lines);
            if (p >= 1) {
                if (buildIntervalRef.current) {
                    clearInterval(buildIntervalRef.current);
                    buildIntervalRef.current = null;
                }
                setPhase('tv_off');
                setTvOffFlash(true);
                setTimeout(() => {
                    start();
                }, TV_OFF_MS);
            }
        }, 50);
    }, [phase, start]);

    // When assets are fully loaded, show "Starting..." then run build animation after delay
    useEffect(() => {
        if (phase !== 'cursor_popup' || progress < 1 || webGLError) return;
        if (!hasShownStartingRef.current) {
            hasShownStartingRef.current = true;
            setTerminalLines(['$ Loading assets...', '', 'All assets loaded.', 'Starting...']);
        }
        setAutoCursorVisible(true);
        const t1 = setTimeout(() => {
            onStartClick();
        }, 3500);
        return () => clearTimeout(t1);
    }, [phase, progress, webGLError, onStartClick]);

    useEffect(() => {
        return () => {
            if (buildIntervalRef.current) clearInterval(buildIntervalRef.current);
        };
    }, []);

    useEffect(() => {
        const el = terminalScrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [terminalLines]);

    const detectWebGLContext = () => {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        return !!(gl && gl instanceof WebGLRenderingContext);
    };

    const loadingDone = overlayOpacity === 0;
    return (
        <div
            style={{
                ...styles.overlay,
                opacity: overlayOpacity,
                transform: loadingDone ? 'none' : 'scale(1)',
                transition: loadingDone ? 'none' : (phase === 'tv_off' ? `opacity 0.05s ease-out` : styles.overlay.transition),
                overflow: loadingDone ? 'hidden' : 'visible',
                display: loadingDone ? 'none' : undefined,
            }}
        >
            {mobilePreWarningVisible && (
                <div style={styles.mobilePreWarningOverlay}>
                    <div style={styles.mobilePreWarningCard}>
                        {isPortrait && <RotatePhoneIcon size={72} />}
                        <p style={styles.mobilePreWarningText}>
                            Best experience on larger screens.
                            {isPortrait && (
                                <>
                                    <br />
                                    For this device, turn to horizontal for a better view.
                                </>
                            )}
                        </p>
                        <p style={styles.mobilePreWarningHint}>
                            Interactive & has sound — best with sound on.
                        </p>
                        <button
                            type="button"
                            onClick={onMobilePreWarningContinue}
                            style={styles.mobilePreWarningContinueBtn}
                        >
                            Continue
                        </button>
                    </div>
                </div>
            )}
            {desktopPreWelcomeVisible && (
                <div style={styles.desktopPreWelcomeOverlay}>
                    <div style={styles.desktopPreWelcomeCard}>
                        <h1 style={styles.desktopPreWelcomeTitle}>Welcome</h1>
                        <p style={styles.desktopPreWelcomeSub}>
                            Best on a large screen with sound on.
                        </p>
                        <button
                            type="button"
                            onClick={onDesktopPreWelcomeStart}
                            className="desktop-pre-welcome-start-btn"
                            style={styles.desktopPreWelcomeStartBtn}
                        >
                            Start
                        </button>
                    </div>
                </div>
            )}
            {tvOffFlash && <div className="cursor-loading-tv-off" style={styles.tvOffFlash} />}

            {cursorPopupVisible && phase !== 'done' && (
                mobileWarning && isPortrait ? (
                    <div style={{ ...styles.popupWrapper, padding: 12 }}>
                        <div style={styles.mobileTerminalOnlyCard}>
                            <div style={styles.mobileTerminalOnlyHeader}>Terminal</div>
                            <div ref={terminalScrollRef} style={styles.mobileTerminalOnlyContent}>
                                {terminalLines.map((line, i) => (
                                    <div key={i} style={styles.terminalLine}>{line}</div>
                                ))}
                                {phase === 'terminal_running' && (
                                    <div style={styles.terminalProgress}>
                                        <div style={styles.progressBarBg}>
                                            <div style={{ ...styles.progressBarFill, width: `${buildProgress}%` }} />
                                        </div>
                                        <span style={styles.progressText}>{Math.round(buildProgress)}%</span>
                                    </div>
                                )}
                            </div>
                                            {mobileWarning && (
                                                <p style={styles.loadingPopupHint}>Interactive & has sound — best with sound on.</p>
                                            )}
                                            <button
                                                ref={startButtonRef}
                                                type="button"
                                                className={`cursor-loading-start-btn${phase === 'cursor_popup' && progress >= 1 ? ' cursor-loading-btn-press' : ''}`}
                                                onClick={onStartClick}
                                                disabled={phase !== 'cursor_popup' || progress < 1}
                                                style={{
                                                    ...styles.startButton,
                                                    opacity: phase === 'cursor_popup' && progress >= 1 ? 1 : 0.6,
                                                    cursor: phase === 'cursor_popup' && progress >= 1 ? 'pointer' : 'default',
                                                    alignSelf: 'center',
                                                    marginTop: 12,
                                                }}
                                            >
                                                Start
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                <div
                    className={`cursor-loading-popup-wrapper${mobileWarning ? ' cursor-loading-mobile' : ''}`}
                    style={{ ...styles.popupWrapper, ...(mobileWarning ? { padding: 12 } : {}) }}
                >
                    <div
                        className="cursor-loading-window cursor-loading-window-apple"
                        style={{
                            ...styles.cursorWindow,
                            ...(mobileWarning ? { maxWidth: '100%', width: '100%', maxHeight: '88%', height: '88%' } : {}),
                        }}
                    >
                        <div className="cursor-loading-title-bar" style={styles.cursorTitleBar}>
                            <div style={styles.trafficLights}>
                                <span style={{ ...styles.trafficDot, backgroundColor: '#ff5f57' }} />
                                <span style={{ ...styles.trafficDot, backgroundColor: '#febc2e' }} />
                                <span style={{ ...styles.trafficDot, backgroundColor: '#28c840' }} />
                            </div>
                            <span style={styles.cursorTitle}>Cursor - portfolio-website</span>
                        </div>
                        <div style={styles.cursorHeaderBar}>
                            <div style={styles.cursorHeaderCenter}>portfolio-website</div>
                            <div style={styles.cursorHeaderRight}>
                                <span style={styles.cursorHeaderIcon} title="Toggle primary sidebar"><HeaderIconSidebarLeft /></span>
                                <span style={styles.cursorHeaderIcon} title="Toggle bottom panel"><HeaderIconPanelBottom /></span>
                                <span style={styles.cursorHeaderIcon} title="Settings"><HeaderIconSettings /></span>
                            </div>
                        </div>
                        <div className="cursor-loading-body" style={styles.cursorBody}>
                            <div className="cursor-loading-activity-bar" style={styles.activityBar}>
                                <div style={styles.activityBarIconActive} title="Explorer">{'\u2630'}</div>
                                <div style={styles.activityBarIcon} title="Search">{'\u2315'}</div>
                                <div style={styles.activityBarIcon} title="Source Control">{'\u2442'}</div>
                                <div style={styles.activityBarIcon} title="Extensions">{'\u25A1'}</div>
                            </div>
                            <div className="cursor-loading-sidebar" style={styles.sidebar}>
                                <div className="cursor-loading-sidebar-tabs-row" style={styles.sidebarTabsRow}>
                                    <span style={styles.sidebarTabActive}>FILES</span>
                                    <span style={styles.sidebarTabInactive}>OUTLINE</span>
                                </div>
                                <div style={styles.fileTree}>{renderFileTree(CURSOR_LOADING_FILE_TREE)}</div>
                            </div>
                            <div className="cursor-loading-main-area" style={styles.mainArea}>
                                <div className="cursor-loading-tabs-row" style={styles.tabsRow}>
                                    <div style={styles.tabActive}>
                                        <span>Application.ts</span>
                                        <span style={styles.tabClose}>×</span>
                                    </div>
                                    <div style={styles.tabInactive}>
                                        <span>LoadingScreen.tsx</span>
                                        <span style={styles.tabClose}>×</span>
                                    </div>
                                </div>
                                <div style={styles.codePanel}>
                                    <CodeBlock code={CURSOR_LOADING_CODE_SNIPPET} lineNumbers style={{ flex: 1, minHeight: 0 }} />
                                </div>
                                <div className="cursor-loading-terminal-panel" style={styles.terminalPanel}>
                                    <div style={styles.terminalHeader}>
                                        <span>TERMINAL</span>
                                        <span style={styles.terminalShellLabel}>zsh</span>
                                        <div style={styles.terminalHeaderRight}>
                                            <button type="button" style={styles.terminalHeaderBtn} aria-label="New terminal">+</button>
                                            <button type="button" style={styles.terminalHeaderBtn} aria-label="Split">{'\u229E'}</button>
                                            <button type="button" style={styles.terminalHeaderBtn} aria-label="Kill terminal">{'\u2715'}</button>
                                        </div>
                                    </div>
                                    <div ref={terminalScrollRef} className="cursor-loading-terminal-content" style={styles.terminalContent}>
                                        {terminalLines.map((line, i) => (
                                            <div key={i} style={styles.terminalLine}>
                                                {line}
                                            </div>
                                        ))}
                                        {phase === 'terminal_running' && (
                                            <div style={styles.terminalProgress}>
                                                <div style={styles.progressBarBg}>
                                                    <div style={{ ...styles.progressBarFill, width: `${buildProgress}%` }} />
                                                </div>
                                                <span style={styles.progressText}>{Math.round(buildProgress)}%</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="cursor-loading-chat-panel" style={styles.chatPanel}>
                                <div style={styles.chatTopRow}>
                                    <div style={styles.chatTabs}>
                                        <span style={styles.chatTabActiveTruncate} title="Portfolio build">Portfolio build</span>
                                        <span style={styles.chatTabInactiveTruncate} title="History">History</span>
                                    </div>
                                    <div style={styles.chatTopRight}>
                                        <button type="button" style={styles.chatTopBtn}>+</button>
                                        <span style={styles.chatTopIcon} title="History">{'\u2316'}</span>
                                        <button type="button" style={styles.chatTopBtn} title="More">{'\u22EE'}</button>
                                    </div>
                                </div>
                                <div style={styles.chatMessages}>
                                    <div style={styles.chatBubble}>
                                        <div style={styles.chatBubbleContent}>
                                            <div style={styles.chatMessage}>
                                                I've built the portfolio app and wired up the 3D scene. The dev server is ready. You can run the start command below to launch it and see the result.
                                            </div>
                                            <div style={styles.chatCommandBlockMulti}>
                                                <code>cd portfolio-website</code>
                                                <code>npm install</code>
                                                <code>npm run dev</code>
                                            </div>
                                                {mobileWarning && (
                                                    <p style={styles.loadingPopupHint}>Interactive & has sound — best with sound on.</p>
                                                )}
                                                <div style={styles.chatCommandBlockActions}>
                                                {autoCursorVisible && phase === 'cursor_popup' && (
                                                    <div style={styles.autoCursor} aria-hidden>
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ pointerEvents: 'none' }}>
                                                            <path d="M5 3l14 9-7 2-2 7-5-18z" fill="#fff" stroke="#333" strokeWidth="1.5" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <button
                                                    ref={startButtonRef}
                                                    type="button"
                                                    className={`cursor-loading-start-btn${phase === 'cursor_popup' && progress >= 1 ? ' cursor-loading-btn-press' : ''}`}
                                                    onClick={onStartClick}
                                                    disabled={phase !== 'cursor_popup' || progress < 1}
                                                    style={{
                                                        ...styles.startButton,
                                                        opacity: phase === 'cursor_popup' && progress >= 1 ? 1 : 0.6,
                                                        cursor: phase === 'cursor_popup' && progress >= 1 ? 'pointer' : 'default',
                                                    }}
                                                >
                                                    Start
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={styles.reviewTextRow}>
                                    <span style={styles.reviewTextItem}>Undo</span>
                                    <span style={styles.reviewTextItem}>Keep</span>
                                    <span style={styles.reviewTextItem}>Review</span>
                                </div>
                                <div style={styles.chatPromptContainer}>
                                    <div style={styles.chatPromptTop}>
                                        <span style={styles.chatModePillActive}>Agent</span>
                                        <span style={styles.chatModePill}>Auto</span>
                                    </div>
                                    <div style={styles.chatPromptBottom}>
                                        <input
                                            type="text"
                                            readOnly
                                            placeholder="Ask anything..."
                                            style={styles.chatInput}
                                        />
                                        <button type="button" style={styles.sendButton} aria-label="Send">
                                            <SendIcon />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )
            )}

            {webGLError && (
                <div style={{ ...styles.popupContainer, opacity: webGLErrorOpacity }}>
                    <div style={styles.errorPopup}>
                        <p><b style={{ color: '#f14c4c' }}>CRITICAL ERROR:</b> No WebGL Detected</p>
                        <p>WebGL is required to run this site. Please enable it or switch to a browser which supports WebGL.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
        border: 'none',
        outline: 'none',
    },
    tvOffFlash: {
        position: 'absolute',
        inset: 0,
        backgroundColor: '#000',
        pointerEvents: 'none',
        zIndex: 15,
        animation: 'cursor-loading-tv-off 0.2s ease-out forwards',
    },
    popupWrapper: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
        padding: 24,
        boxSizing: 'border-box',
    },
    cursorWindow: {
        width: '100%',
        maxWidth: 960,
        height: '80%',
        maxHeight: 620,
        backgroundColor: '#1e1e1e',
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.06)',
    },
    cursorTitleBar: {
        height: 38,
        backgroundColor: '#323233',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 0,
        paddingRight: 12,
        flexShrink: 0,
        gap: 12,
    },
    trafficLights: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 14,
    },
    trafficDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        display: 'inline-block',
    },
    cursorTitle: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.85)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 400,
    },
    cursorHeaderBar: {
        position: 'relative',
        height: 28,
        backgroundColor: '#323233',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 12,
        paddingRight: 12,
        flexShrink: 0,
    },
    cursorHeaderCenter: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.75)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    cursorHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginLeft: 'auto',
    },
    cursorHeaderIcon: {
        width: 28,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.75)',
    },
    cursorBody: {
        flex: 1,
        display: 'flex',
        minHeight: 0,
    },
    activityBar: {
        width: 48,
        backgroundColor: '#252526',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 8,
        gap: 4,
        flexShrink: 0,
    },
    activityBarIcon: {
        width: 48,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        color: 'rgba(255,255,255,0.6)',
    },
    activityBarIconActive: {
        width: 48,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        color: 'rgba(255,255,255,0.95)',
        borderLeft: '2px solid rgba(255,255,255,0.8)',
        boxSizing: 'border-box',
    },
    sidebar: {
        width: 200,
        backgroundColor: '#252526',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        minWidth: 0,
    },
    sidebarTabsRow: {
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
    },
    sidebarTabActive: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 600,
        padding: '4px 10px',
        backgroundColor: '#1e1e1e',
        borderRadius: 4,
    },
    sidebarTabInactive: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '4px 8px',
    },
    fileTree: {
        paddingLeft: 6,
        paddingTop: 6,
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    mainArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    tabsRow: {
        height: 32,
        backgroundColor: '#252526',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 8,
        gap: 2,
        flexShrink: 0,
    },
    tabActive: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 10,
        paddingRight: 8,
        backgroundColor: '#1e1e1e',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.9)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    tabInactive: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 10,
        paddingRight: 8,
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    tabClose: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.5)',
        lineHeight: 1,
    },
    codePanel: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        backgroundColor: '#1e1e1e',
    },
    codeContent: {
        padding: 12,
    },
    pre: {
        margin: 0,
        fontFamily: 'ui-monospace, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        color: '#d4d4d4',
    },
    code: {
        whiteSpace: 'pre',
        color: '#9cdcfe',
    },
    terminalPanel: {
        height: 120,
        backgroundColor: '#1e1e1e',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    terminalHeader: {
        height: 28,
        backgroundColor: '#323233',
        fontSize: 11,
        color: '#ccc',
        paddingLeft: 12,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    terminalShellLabel: {
        marginLeft: 8,
        color: 'rgba(255,255,255,0.45)',
        fontSize: 10,
    },
    terminalHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    terminalHeaderBtn: {
        width: 24,
        height: 22,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 14,
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    terminalContent: {
        flex: 1,
        padding: 8,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontSize: 12,
        fontFamily: 'ui-monospace, "SF Mono", Monaco, monospace',
        color: '#cccccc',
    },
    terminalLine: {
        lineHeight: 1.4,
    },
    terminalProgress: {
        marginTop: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    progressBarBg: {
        flex: 1,
        height: 6,
        backgroundColor: '#3c3c3c',
        borderRadius: 3,
        overflow: 'hidden',
    },
    progressBarFill: {
        height: '100%',
        backgroundColor: '#0e639c',
        transition: 'width 0.1s linear',
    },
    progressText: {
        fontSize: 11,
        color: '#888',
        minWidth: 36,
    },
    chatPanel: {
        position: 'relative',
        width: 280,
        backgroundColor: '#252526',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    chatTopRow: {
        height: 36,
        paddingLeft: 12,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
    },
    chatTopLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    chatTabs: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
        minWidth: 0,
    },
    chatTabActive: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 500,
    },
    chatTabInactive: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.45)',
    },
    chatTabActiveTruncate: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 500,
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chatTabInactiveTruncate: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.45)',
        maxWidth: 120,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    chatTopRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    chatTopBtn: {
        width: 24,
        height: 24,
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    chatTopIcon: {
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        color: 'rgba(255,255,255,0.6)',
    },
    chatHeader: {
        height: 36,
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
        paddingLeft: 12,
        paddingRight: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 12,
    },
    chatBubble: {
        display: 'flex',
        gap: 10,
        marginBottom: 12,
    },
    chatBubbleContent: {
        flex: 1,
        minWidth: 0,
    },
    chatMessage: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 1.5,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        marginBottom: 10,
    },
    chatCommandBlock: {
        padding: '8px 12px',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: 6,
        marginBottom: 10,
        fontFamily: 'ui-monospace, "SF Mono", Monaco, monospace',
        fontSize: 12,
        color: 'rgba(255,255,255,0.9)',
    },
    chatCommandBlockMulti: {
        padding: '8px 12px',
        backgroundColor: 'rgba(0,0,0,0.35)',
        borderRadius: 6,
        marginBottom: 8,
        fontFamily: 'ui-monospace, "SF Mono", Monaco, monospace',
        fontSize: 12,
        color: 'rgba(255,255,255,0.9)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    chatCommandBlockActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 4,
        position: 'relative',
    },
    autoCursor: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        animation: 'cursor-loading-click 2s ease-in-out 1.5s both',
    },
    startButton: {
        padding: '6px 16px',
        backgroundColor: 'rgba(0,122,255,0.4)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 6,
        fontSize: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: 'rgba(255,255,255,0.95)',
    },
    reviewTextRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 12px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backgroundColor: 'rgba(0,0,0,0.1)',
        flexShrink: 0,
    },
    reviewTextItem: {
        fontSize: 11,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: 'rgba(255,255,255,0.7)',
    },
    chatPromptContainer: {
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: 10,
        backgroundColor: 'rgba(0,0,0,0.15)',
    },
    chatPromptTop: {
        display: 'flex',
        gap: 6,
        marginBottom: 8,
    },
    chatModePillActive: {
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 4,
        backgroundColor: 'rgba(0,122,255,0.35)',
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatModePill: {
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 4,
        color: 'rgba(255,255,255,0.5)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    chatPromptBottom: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    chatInputRow: {
        display: 'flex',
        gap: 8,
        padding: 10,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        alignItems: 'center',
    },
    chatInput: {
        flex: 1,
        height: 32,
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8,
        color: 'rgba(255,255,255,0.9)',
        padding: '0 12px',
        fontSize: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    sendButton: {
        height: 32,
        width: 32,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.12)',
        color: 'rgba(255,255,255,0.8)',
        border: 'none',
        borderRadius: 6,
        cursor: 'default',
    },
    popupContainer: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20,
    },
    errorPopup: {
        backgroundColor: '#1e1e1e',
        padding: 24,
        border: '1px solid #333',
        borderRadius: 8,
        maxWidth: 440,
        color: '#d4d4d4',
        fontFamily: 'monospace',
        fontSize: 14,
    },
    mobilePreWarningOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
        backgroundColor: 'rgba(0,0,0,0.95)',
    },
    mobilePreWarningCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        maxWidth: 320,
        textAlign: 'center',
    },
    mobilePreWarningText: {
        fontSize: 15,
        lineHeight: 1.4,
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        margin: 0,
    },
    mobilePreWarningHint: {
        fontSize: 13,
        lineHeight: 1.35,
        color: 'rgba(255,255,255,0.75)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        margin: 0,
    },
    loadingPopupHint: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        margin: '0 0 8px 0',
    },
    mobilePreWarningContinueBtn: {
        minHeight: 48,
        minWidth: 140,
        padding: '12px 24px',
        fontSize: 15,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 500,
        color: '#000',
        backgroundColor: 'rgba(255,255,255,0.95)',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
    },
    desktopPreWelcomeOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
        backgroundColor: '#000',
    },
    desktopPreWelcomeCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        maxWidth: 320,
        textAlign: 'center',
    },
    desktopPreWelcomeTitle: {
        margin: 0,
        fontSize: 24,
        fontWeight: 500,
        color: 'rgba(255,255,255,0.95)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    desktopPreWelcomeSub: {
        margin: 0,
        fontSize: 15,
        lineHeight: 1.4,
        color: 'rgba(255,255,255,0.8)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    desktopPreWelcomeStartBtn: {
        minHeight: 48,
        minWidth: 140,
        padding: '12px 24px',
        fontSize: 15,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: 500,
        color: '#000',
        backgroundColor: 'rgba(255,255,255,0.95)',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
    },
    mobileTerminalOnlyCard: {
        width: '100%',
        maxWidth: 480,
        maxHeight: '85%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1e1e1e',
        borderRadius: 12,
        overflow: 'hidden',
        padding: 16,
        boxSizing: 'border-box',
    },
    mobileTerminalOnlyHeader: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        marginBottom: 8,
    },
    mobileTerminalOnlyContent: {
        flex: 1,
        minHeight: 120,
        padding: 8,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontSize: 11,
        fontFamily: 'ui-monospace, "SF Mono", Monaco, monospace',
        color: '#cccccc',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: 6,
    },
};

export default LoadingScreen;
